import _ from 'lodash'
import pathToRegexp from 'path-to-regexp'
import PageRoutes from 'todistukset-ui/src/vtj/todistukset-page-routes.enum'
import StepRoutes from 'todistukset-ui/src/vtj/todistukset-step-routes.enum'
import Pages from 'todistukset-ui/src/vtj/components/page-router/todistukset-pages.enum'
import supportedLanguages from 'todistukset-ui/src/vtj/page-lang-options'
import { PageConfig } from 'todistukset-ui/src/vtj/components/page-router/TodistuksetRouter.types'
import { Tabs } from 'todistukset-ui/src/vtj/components/tab-navigation/todistus-tabs.enum'

export const vtjOteTodistusPages = [
  PageRoutes.ELOSSAOLOTODISTUS,
  PageRoutes.SYNTYMATODISTUS,
  PageRoutes.ASUINPAIKKATODISTUS,
  PageRoutes.SIVIILISAATYTODISTUS,
  PageRoutes.AVIOLIITTOTODISTUS,
]

// ORDER_COMPLETE is different, it requires :paymentId parameter
const vtjOteSteps = [
  StepRoutes.INSTRUCTIONS,
  StepRoutes.EIDAS_PERSONAL_INFORMATION,
  StepRoutes.BASIC_INFORMATION,
  StepRoutes.USAGE_REGION,
  StepRoutes.ADDITIONAL_SELECTIONS,
  StepRoutes.DELIVERY_METHOD,
  StepRoutes.SUMMARY,
]

const aetOrdererSteps = [
  StepRoutes.AET_INSTRUCTIONS,
  StepRoutes.AET_ORDERER_INFO,
  StepRoutes.AET_SPOUSE_INFO,
  StepRoutes.AET_LASTNAME_CHOICE,
  StepRoutes.AET_ADDITIONAL_SELECTIONS,
  StepRoutes.AET_CONTACT_INFO,
  StepRoutes.AET_SUMMARY,
  StepRoutes.AET_ORDER_COMPLETE,
]

const aetSignerSteps = [
  StepRoutes.AET_SIGNER_INSTRUCTIONS,
  StepRoutes.AET_SIGNER_ORDERER_INFO,
  StepRoutes.AET_SIGNER_SPOUSE_INFO,
  StepRoutes.AET_SIGNER_LASTNAME_CHOICE,
  StepRoutes.AET_SIGNER_CONTACT_INFO,
  StepRoutes.AET_SIGNER_SUMMARY,
  StepRoutes.AET_SIGNER_ORDER_COMPLETE,
  StepRoutes.AET_SIGNER_TODISTUSPYYNTO_REJECTED,
]

// UAET_ORDER_COMPLETE is different, it requires optionally :paymentId parameter
const uaetSteps = [
  StepRoutes.UAET_INSTRUCTIONS,
  StepRoutes.UAET_ORDERER_INFO,
  StepRoutes.UAET_SPOUSE_INFO,
  StepRoutes.UAET_USAGE_REGION,
  StepRoutes.UAET_ADDITIONAL_SELECTIONS,
  StepRoutes.UAET_CONTACT_INFO,
  StepRoutes.UAET_SUMMARY,
]

export function getPage({
  page,
  tab,
}: {
  page: PageRoutes | undefined
  tab: PageRoutes | undefined
}): { visiblePage: PageConfig; pageRoute: PageRoutes } {
  return {
    visiblePage: Pages[tab || page || PageRoutes.LANDING],
    pageRoute: tab || page || PageRoutes.LANDING,
  }
}

function createFormPaths() {
  const todistusTypeRoutes = vtjOteTodistusPages.flatMap((page) => [
    `/${page}`,
    ...vtjOteSteps.map((step) => `/${page}/${step}`),
    `/${page}/${StepRoutes.ORDER_COMPLETE}/:paymentId`,
  ])

  const aetOrdererRoutes = [
    `/${PageRoutes.AETTODISTUS_ORDERER}`,
    ...aetOrdererSteps.map(
      (step) => `/${PageRoutes.AETTODISTUS_ORDERER}/${step}`
    ),
  ]

  const aetSignerRoutes = [
    `/${PageRoutes.AETTODISTUS_SIGNER}`,
    ...aetSignerSteps.map(
      (step) => `/${PageRoutes.AETTODISTUS_SIGNER}/:aetTodistuspyyntoId/${step}`
    ),
  ]

  const uaetRoutes = [
    `/${PageRoutes.UAETTODISTUS}`,
    ...uaetSteps.map((step) => `/${PageRoutes.UAETTODISTUS}/${step}`),
    `/${PageRoutes.UAETTODISTUS}/${StepRoutes.UAET_ORDER_COMPLETE}/:paymentId?`,
  ]

  const routes = todistusTypeRoutes.concat(
    aetOrdererRoutes,
    aetSignerRoutes,
    uaetRoutes
  )

  return routes
}

export const createSupportedRoutePatterns = (): string[] => {
  const createPattern = (paths: string[]) => {
    return _.chain(paths)
      .map((path) => [
        `${path}`,
        `${path}/lisatiedot`,
        `${path}/omat-todistukset`,
        ...supportedLanguages().map((lang) => `/${lang}${path}`),
        ...supportedLanguages().map((lang) => `/${lang}${path}/lisatiedot`),
        ...supportedLanguages().map(
          (lang) => `/${lang}${path}/omat-todistukset`
        ),
      ])
      .flatten()
      .value()
  }
  const landingPattern = createPattern([`/etusivu`, ''])
  const formPattern = createPattern(createFormPaths())

  return [...landingPattern, ...formPattern]
}

const routeMatcher = (route: string) => {
  // Mapping path-to-regexp's positional results into named results.
  // A newer version of path-to-regexp would have the equivalent of this as `match()`
  const keys: pathToRegexp.Key[] = [] // populated by pathToRegexp call below
  const regex = pathToRegexp(route, keys, { sensitive: true, endsWith: '#' })
  return (path: string): { [k: string]: string | undefined } | undefined => {
    const matches = regex.exec(path)
    if (!matches) {
      return undefined
    }
    return matches.slice(1).reduce((acc, cur, index) => {
      acc[keys[index].name] = cur
      return acc
    }, {} as { [k: string]: string | undefined })
  }
}

const langRegex = supportedLanguages().join('|')
const tabsRegex = [Tabs.INFO, Tabs.MY_CERTIFICATES].join('|')
const vtjOtePagesRegex = vtjOteTodistusPages.join('|')
const vtjOteStepsRegex = vtjOteSteps.join('|')
const aetOrdererStepsRegex = aetOrdererSteps.join('|')
const aetSignerStepsRegex = aetSignerSteps.join('|')
const uaetStepsRegex = uaetSteps.join('|')

const orderCompleteMatcher = routeMatcher(
  `/:lang(${langRegex})?/:page(${vtjOtePagesRegex}|${PageRoutes.UAETTODISTUS})/:step(${StepRoutes.ORDER_COMPLETE}|${StepRoutes.UAET_ORDER_COMPLETE})/:paymentId([0-9]+)/:tab(${tabsRegex})?`
)
const vtjOteTodistusMatcher = routeMatcher(
  `/:lang(${langRegex})?/:page(${vtjOtePagesRegex})/:step(${vtjOteStepsRegex})?/:tab(${tabsRegex})?`
)
const aetTodistusOrdererMatcher = routeMatcher(
  `/:lang(${langRegex})?/:page(${PageRoutes.AETTODISTUS_ORDERER})/:step(${aetOrdererStepsRegex})?/:tab(${tabsRegex})?`
)
const aetTodistusSignerMatcher = routeMatcher(
  `/:lang(${langRegex})?/:page(${PageRoutes.AETTODISTUS_SIGNER})/:aetTodistuspyyntoId/:step(${aetSignerStepsRegex})/:tab(${tabsRegex})?`
)
const uaetTodistusMatcher = routeMatcher(
  `/:lang(${langRegex})?/:page(${PageRoutes.UAETTODISTUS})/:step(${uaetStepsRegex})?/:tab(${tabsRegex})?`
)
const frontTabsMatcher = routeMatcher(
  `/:lang(${langRegex})?/:tab(${tabsRegex})?`
)

export const parseComponentSelectionFromPath = (
  path: string
): {
  page: PageRoutes | undefined
  step: StepRoutes | undefined
  tab: PageRoutes | undefined
  lang: string | undefined
  paymentId: string | undefined
} => {
  function isPageRoutes(input: unknown): input is PageRoutes {
    return Object.values<unknown>(PageRoutes).includes(input)
  }

  function isStepRoutes(input: unknown): input is StepRoutes {
    return Object.values<unknown>(StepRoutes).includes(input)
  }

  const { page, step, tab, lang, paymentId } =
    orderCompleteMatcher(path) ??
    vtjOteTodistusMatcher(path) ??
    aetTodistusOrdererMatcher(path) ??
    aetTodistusSignerMatcher(path) ??
    uaetTodistusMatcher(path) ??
    frontTabsMatcher(path) ??
    {}
  return {
    page: isPageRoutes(page) ? page : undefined,
    step: isStepRoutes(step) ? step : undefined,
    tab: isPageRoutes(tab) ? tab : undefined,
    lang,
    paymentId,
  }
}
